import React, { useCallback, useRef } from 'react';
import { FiMail, FiLock } from 'react-icons/fi';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import getValidationErrors from '../../utils/getValidationErrors';

import logoImg from '../../assets/eb-logo-white.png';

import Input from '../../components/Input';
import Button from '../../components/Button';

import { Container, Content, AnimationContainer, Background } from './styles';
import useFavicon, { DEFAULT_FAVICON_FILE_NAME } from '../../hooks/useFavicon';

interface SignInFormData {
  email: string;
  password: string;
}

const SignIn: React.FC = () => {
  // Public page of the back office: `public/index.html` only carries a neutral
  // placeholder favicon, so the platform one is applied here.
  useFavicon({
    faviconFileName: DEFAULT_FAVICON_FILE_NAME,
    persist: false,
  });

  const formRef = useRef<FormHandles>(null);

  const { signIn } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (data: SignInFormData) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          email: Yup.string()
            .required('Username is required')
            .email('Enter a valid email address'),
          password: Yup.string().required('Password is required'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await signIn({
          email: data.email,
          password: data.password,
        });

        navigate('/projects');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);

          return;
        }

        addToast({
          type: 'error',
          title: 'Authentication Error',
          description: 'There was an error signing in, check credentials.',
        });
      }
    },
    [signIn, navigate, addToast],
  );

  return (
    <Container>
      <Content>
        <AnimationContainer>
          <img src={logoImg} alt="Victor" />
          <Form ref={formRef} onSubmit={handleSubmit}>
            <Input
              name="email"
              icon={FiMail}
              placeholder="Username"
              maxLength={50}
            />
            <Input
              name="password"
              icon={FiLock}
              type="password"
              placeholder="Password"
            />

            <Button type="submit">Login</Button>
          </Form>
        </AnimationContainer>
      </Content>

      <Background />
    </Container>
  );
};

export default SignIn;
